import React, { useState, useEffect } from 'react';
import { VisitorForm } from './VisitorForm'; // Ensure this matches your filename
import { Loader2, Sparkles, CheckCircle2, ChevronDown, FileText, ClipboardList, Clock } from 'lucide-react';

export const VisitorQuestionnaire = ({ shopId }: { shopId: number }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal Information
    title: '',
    name: '',
    postalAddress: '',
    postalCode: '',
    townCity: '',
    estate: '',
    houseNo: '',
    street: '',
    email: '',
    profession: '',
    placeOfWork: '',
    
    // Church Information
    isBaptized: '',
    churchBaptizedIn: '',
    isConfirmed: '',
    churchConfirmedIn: '',
    isCommunicant: '',
    isBornAgain: '',
    formerChurchName: '',
    formerChurchPlace: '',
    talentsGifts: '',

    // Family Information
    maritalStatus: '',
    spouseName: '',
    childrenNames: '',
    nextOfKinName: '',
    nextOfKinRelationship: '',
    nextOfKinContact: '',

    // Commitment
    registrationType: 'New', // Default based on form
    membershipNo: '',
    otherCommitments: ''
  });

  const [commitments, setCommitments] = useState<string[]>([]);

  const toggleCommitment = (item: string) => {
    setCommitments(prev => 
      prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      ...formData,
      selectedCommitments: commitments,
      shop_id: shopId,
      submitted_at: new Date().toISOString(),
    };

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/church-visitor-responses', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setIsSuccess(true);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Submission Error:', error);
      alert("Registration failed. Please verify your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClasses = "w-full p-3.5 bg-gray-50 border border-blue-50/50 rounded-xl font-bold text-gray-900 placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-blue-400 outline-none transition-all disabled:opacity-50 text-sm shadow-inner";
  const labelClasses = "block text-[10px] font-black text-blue-600 uppercase tracking-wider mb-1 ml-1";
  const sectionTitleClasses = "text-sm font-black text-gray-900 uppercase border-b border-gray-100 pb-1 mt-6 mb-3 tracking-wide flex items-center gap-2";

  if (isSuccess) {
    return (
      <div className="text-center p-8 bg-emerald-50/50 border border-emerald-100 rounded-3xl animate-in fade-in duration-300">
        <h3 className="text-xl font-black text-emerald-800">Praise God!</h3>
        <p className="text-sm text-emerald-600 font-bold mt-2">Your Membership Registration Form has been submitted successfully.</p>
        <p className="text-xs text-gray-500 mt-4">Kindly proceed with the processing fee payment (Kshs. 200) via Pay bill 4036261.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-w-md mx-auto p-1 animate-in fade-in slide-in-from-bottom-2 duration-500 text-left">
      
      {/* SECTION 1: PERSONAL INFORMATION */}
      <h2 className={sectionTitleClasses}>👤 Personal Information</h2>
      
      <div className="flex flex-col sm:grid sm:grid-cols-3 gap-3">
        <div className="col-span-1">
          <label className={labelClasses}>Title</label>
          <select name="title" value={formData.title} onChange={handleChange} className={inputClasses} required>
            <option value="">Select...</option>
            <option value="Mr.">Mr.</option>
            <option value="Mrs.">Mrs.</option>
            <option value="Miss">Miss</option>
            <option value="Dr.">Dr.</option>
            <option value="Eng.">Eng.</option>
            <option value="Prof.">Prof.</option>
            <option value="Rev.">Rev.</option>
            <option value="Ven.">Ven.</option>
          </select>
        </div>
        <div className="col-span-2">
          <label className={labelClasses}>Full Name</label>
          <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="John Doe" className={inputClasses} required />
        </div>
      </div>

      <div className="flex flex-col sm:grid sm:grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className={labelClasses}>Postal Address</label>
          <input type="text" name="postalAddress" value={formData.postalAddress} onChange={handleChange} placeholder="P.O. Box..." className={inputClasses} />
        </div>
        <div className="col-span-1">
          <label className={labelClasses}>Code</label>
          <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} placeholder="00100" className={inputClasses} />
        </div>
      </div>

      <div>
        <label className={labelClasses}>Town / City</label>
        <input type="text" name="townCity" value={formData.townCity} onChange={handleChange} placeholder="Nairobi" className={inputClasses} />
      </div>

      <div className="flex flex-col sm:grid sm:grid-cols-3 gap-3">
        <div className="col-span-1">
          <label className={labelClasses}>Estate</label>
          <input type="text" name="estate" value={formData.estate} onChange={handleChange} placeholder="Otiende" className={inputClasses} />
        </div>
        <div className="col-span-1">
          <label className={labelClasses}>House No.</label>
          <input type="text" name="houseNo" value={formData.houseNo} onChange={handleChange} placeholder="A1" className={inputClasses} />
        </div>
        <div className="col-span-1">
          <label className={labelClasses}>Street</label>
          <input type="text" name="street" value={formData.street} onChange={handleChange} placeholder="Street name" className={inputClasses} />
        </div>
      </div>

      <div className="flex flex-col sm:grid sm:grid-cols-3 gap-3">
        <div>
          <label className={labelClasses}>Profession / Occupation</label>
          <input type="text" name="profession" value={formData.profession} onChange={handleChange} placeholder="Accountant" className={inputClasses} />
        </div>
        <div>
          <label className={labelClasses}>Place of Work</label>
          <input type="text" name="placeOfWork" value={formData.placeOfWork} onChange={handleChange} placeholder="Company Name" className={inputClasses} />
        </div>
      </div>

      {/* SECTION 2: CHURCH INFORMATION */}
      <h2 className={sectionTitleClasses}>⛪ Church Information</h2>

      <div className="flex flex-col sm:grid sm:grid-cols-3 gap-3">
        <div>
          <label className={labelClasses}>Are you Baptized?</label>
          <select name="isBaptized" value={formData.isBaptized} onChange={handleChange} className={inputClasses}>
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        <div>
          <label className={labelClasses}>If yes, Church Baptized in</label>
          <input type="text" name="churchBaptizedIn" value={formData.churchBaptizedIn} onChange={handleChange} placeholder="Church name" className={inputClasses} disabled={formData.isBaptized !== 'Yes'} />
        </div>
      </div>

      <div className="flex flex-col sm:grid sm:grid-cols-3 gap-3">
        <div>
          <label className={labelClasses}>Are you Confirmed?</label>
          <select name="isConfirmed" value={formData.isConfirmed} onChange={handleChange} className={inputClasses}>
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        <div>
          <label className={labelClasses}>If yes, Church Confirmed in</label>
          <input type="text" name="churchConfirmedIn" value={formData.churchConfirmedIn} onChange={handleChange} placeholder="Church name" className={inputClasses} disabled={formData.isConfirmed !== 'Yes'} />
        </div>
      </div>

      <div className="flex flex-col sm:grid sm:grid-cols-3 gap-3">
        <div>
          <label className={labelClasses}>Are you a Communicant?</label>
          <select name="isCommunicant" value={formData.isCommunicant} onChange={handleChange} className={inputClasses}>
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        <div>
          <label className={labelClasses}>Are you born again Christian?</label>
          <select name="isBornAgain" value={formData.isBornAgain} onChange={handleChange} className={inputClasses}>
            <option value="">Select...</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
      </div>

      <div className="flex flex-col sm:grid sm:grid-cols-3 gap-3">
        <div>
          <label className={labelClasses}>Name of former Church</label>
          <input type="text" name="formerChurchName" value={formData.formerChurchName} onChange={handleChange} placeholder="Former church" className={inputClasses} />
        </div>
        <div>
          <label className={labelClasses}>Place</label>
          <input type="text" name="formerChurchPlace" value={formData.formerChurchPlace} onChange={handleChange} placeholder="Location" className={inputClasses} />
        </div>
      </div>

      <div>
        <label className={labelClasses}>What are your talents & gifts in the Church?</label>
        <input type="text" name="talentsGifts" value={formData.talentsGifts} onChange={handleChange} placeholder="List your gifts and talents" className={inputClasses} />
      </div>

      {/* SECTION 3: FAMILY INFORMATION */}
      <h2 className={sectionTitleClasses}>👨‍👩‍👧‍👦 Family Information</h2>

      <div>
        <label className={labelClasses}>Marital Status</label>
        <select name="maritalStatus" value={formData.maritalStatus} onChange={handleChange} className={inputClasses}>
          <option value="">Select...</option>
          <option value="Married">Married</option>
          <option value="Single">Single</option>
          <option value="Widow">Widow</option>
          <option value="Widower">Widower</option>
          <option value="Separation">Separation</option>
        </select>
      </div>

      <div>
        <label className={labelClasses}>Name of the Spouse</label>
        <input type="text" name="spouseName" value={formData.spouseName} onChange={handleChange} placeholder="Spouse full name" className={inputClasses} disabled={formData.maritalStatus !== 'Married'} />
      </div>

      <div>
        <label className={labelClasses}>Name of the Child(ren)</label>
        <textarea name="childrenNames" rows={2} value={formData.childrenNames} onChange={handleChange} placeholder="Names of children (comma separated)" className={`${inputClasses} resize-none`} />
      </div>

      <div className="flex flex-col sm:grid sm:grid-cols-3 gap-3">
        <div className="col-span-1">
          <label className={labelClasses}>Next of Kin</label>
          <input type="text" name="nextOfKinName" value={formData.nextOfKinName} onChange={handleChange} placeholder="Name" className={inputClasses} />
        </div>
        <div className="col-span-1">
          <label className={labelClasses}>Relationship</label>
          <input type="text" name="nextOfKinRelationship" value={formData.nextOfKinRelationship} onChange={handleChange} placeholder="e.g., Brother" className={inputClasses} />
        </div>
        <div className="col-span-1">
          <label className={labelClasses}>Contact</label>
          <input type="text" name="nextOfKinContact" value={formData.nextOfKinContact} onChange={handleChange} placeholder="Phone" className={inputClasses} />
        </div>
      </div>

      {/* SECTION 4: COMMITMENT */}
      <h2 className={sectionTitleClasses}>✍️ Commitment Areas</h2>
      <p className="text-[11px] font-bold text-gray-400 -mt-2 mb-2 ml-1">Select the groups/fellowships you purpose to serve God in:</p>
      
      <div className="flex flex-wrap gap-2 mb-2">
        {['Zone fellowship', 'KAMA/MEN', 'M. U/Ladies', 'NDYM/Youth', 'Praise & Worship', 'Choir', 'T.E.E', 'Ushering', 'Hospitality', 'Mission & Evangelism'].map((item) => {
          const isSelected = commitments.includes(item);
          return (
            <button
              type="button" key={item} onClick={() => toggleCommitment(item)}
              className={`px-3 py-2 rounded-xl text-xs font-black transition-all border ${
                isSelected 
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm' 
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {item}
            </button>
          );
        })}
      </div>

      <div>
        <label className={labelClasses}>Any other commitment area</label>
        <input type="text" name="otherCommitments" value={formData.otherCommitments} onChange={handleChange} placeholder="Specify other group..." className={inputClasses} />
      </div>

      <div className="flex flex-col sm:grid sm:grid-cols-2 gap-4 border-t border-gray-100 pt-4 mt-4">
        <div>
          <label className={labelClasses}>Registration Type</label>
          <select name="registrationType" value={formData.registrationType} onChange={handleChange} className={inputClasses}>
            <option value="New">New Registration</option>
            <option value="Renewal">Renewal</option>
          </select>
        </div>
        <div>
          <label className={labelClasses}>Membership No. (If Renewal)</label>
          <input type="text" name="membershipNo" value={formData.membershipNo} onChange={handleChange} placeholder="e.g., SBO-042" className={inputClasses} disabled={formData.registrationType !== 'Renewal'} />
        </div>
      </div>

      {/* SECURE SUBMIT BUTTON */}
      <button 
        type="submit" disabled={isSubmitting}
        className="w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-[2rem] font-black shadow-md flex items-center justify-center gap-2 active:scale-95 transition-all text-base tracking-wide mt-6"
      >
        {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : <><Sparkles size={18} /> SUBMIT MEMBERSHIP REGISTRATION</>}
      </button>
    </form>
  );
};

// Using a Named Export to match your App.tsx import
export const VisitorWelcomePage = ({ shopId }: { shopId: number }) => {
  const [hasCheckedIn, setHasCheckedIn] = useState(false);

  const [serviceData, setServiceData] = useState<any[]>([]);

  const [selectedHubItem, setSelectedHubItem] = useState('menu'); // 'menu', 'package', 'oos', 'overview', 'survey'

  const [selectedService, setSelectedService] = useState('English Service');

  useEffect(() => {
    if (hasCheckedIn && selectedHubItem === 'oos') {
      const getOrderOfService = async () => {
        try {

          setServiceData([]);

          const response = await fetch('https://n8n.tenear.com/webhook/fetch-visitors-service-order', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              shop_id: shopId,
              service_type: selectedService 
            })
          });
          

          if (response.ok) {
            const n8nData = await response.json();
            
            // 1. Normalize the data exactly like your authenticated logic
            const rawServices = Array.isArray(n8nData) ? n8nData[0] : (n8nData.services ? n8nData.services[0] : n8nData);

            // 2. Extract the activities from the service
            if (rawServices && rawServices.service_activities) {
              setServiceData(rawServices.service_activities);
            }
          }
        } catch (err) {
          console.error("Fetch error:", err);
        }
      };
      getOrderOfService();
    }
  }, [hasCheckedIn, shopId, selectedService, selectedHubItem]);


  return (
    <div className="max-w-md mx-auto bg-white min-h-screen p-8 space-y-8">
      {!hasCheckedIn ? (
        <>
          <div className="text-center">
            <img 
              src="https://ufrrlfcxuovxgizxuowh.supabase.co/storage/v1/object/public/church-logos/StBarnanasGoldenFinal27apr.jpeg" 
              className="w-20 h-20 mx-auto mb-4 drop-shadow-sm" 
              alt="St. Barnabas Logo"
            />
            <h2 className="text-2xl font-black text-gray-500 leading-tight">Welcome Home!</h2>
            <p className="text-lg text-blue-400 font-chancery mt-3 px-4">
              Feel welcome at ACK St. Barnabas Otiende.
              The home of encouragement!
            </p>
            <p className="text-base text-blue-400 font-chancery mt-2 italic">
              Kindly share with us your names and contacts below:                       
            </p>
          </div>
        
          <VisitorForm onComplete={() => setHasCheckedIn(true)} />
        </>
      ) : (
        <div className="space-y-6 animate-in fade-in duration-700">
          <div className="flex items-center gap-3 bg-green-50 p-4 rounded-2xl border border-green-100 text-green-700">
            <CheckCircle2 size={24} className="flex-shrink-0" />
            <p className="font-bold text-sm text-left leading-tight">
              Praise God! You are checked in.
            </p>
          </div>

          {/* 1. SHOW THE MENU ONLY IF NOTHING IS SELECTED */}
          {selectedHubItem === 'menu' ? (
            <div className="space-y-2">
              <HubButton 
                icon={<FileText size={18} />} 
                  label="Welcome Package" 
                  onClick={() => window.open('https://ufrrlfcxuovxgizxuowh.supabase.co/storage/v1/object/public/church_material/StBarnabasVisitorWelcomePage.pdf', '_blank')} 
                />
                <HubButton 
                  icon={<Clock size={18} />} 
                  label="Today's Order of Service" 
                  onClick={() => setSelectedHubItem('oos')} 
                />
                <HubButton 
                  icon={<FileText size={18} />} 
                  label="Overview of ACK St. Barnabas" 
                  onClick={() => window.open('https://ufrrlfcxuovxgizxuowh.supabase.co/storage/v1/object/public/church_material/StBarnabasVisitorWelcomePage.pdf', '_blank')}
                />
                <HubButton 
                  icon={<ClipboardList size={18} />} 
                  label="Brief Questionnaire" 
                  onClick={() => setSelectedHubItem('survey')} 
                />
              </div>
            ) : (
              /* 2. SHOW THE BACK BUTTON IF SOMETHING IS SELECTED */
              <button 
                onClick={() => setSelectedHubItem('menu')}
                className="flex items-center gap-2 text-xs font-black text-blue-600 uppercase tracking-widest bg-blue-50 px-4 py-2 rounded-full hover:bg-blue-100 transition-all"
              >
                ← Back to Menu
              </button>
            )}
    
            {/* 3. DYNAMIC CONTENT: OOS  */}
            {selectedHubItem === 'oos' && (
              <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
                {/* SERVICE TABS */}
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  {['Morning Glory', 'Youth Service', 'English Service', 'Kiswahili Service'].map((service) => (
                    <button
                      key={service}
                      onClick={() => setSelectedService(service)}
                      className={`px-4 py-2 rounded-full text-[10px] font-black whitespace-nowrap transition-all border ${
                        selectedService === service 
                          ? 'bg-blue-600 text-white border-blue-600 shadow-md' 
                          : 'bg-white text-gray-500 border-gray-100'
                      }`}
                    >
                      {service.toUpperCase()}
                    </button>
                  ))}
                </div>

                <h3 className="text-xs font-black text-blue-600 uppercase tracking-widest border-b pb-2">
                  {selectedService} Program
                </h3>
          
                {serviceData.length > 0 ? (
                  serviceData.map((activity, index) => (
                    <div key={index} className="flex gap-4 items-start p-4 bg-gray-50 rounded-[1.5rem] border border-gray-100 shadow-sm">
                      <div className="bg-blue-600 text-white text-[10px] font-black w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        {index + 1}
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-black text-gray-800 leading-tight">{activity.activity_name}</p>
                        <p className="text-[11px] font-medium text-gray-500 italic">{activity.description}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-10">
                    <Loader2 className="animate-spin mx-auto text-gray-300" size={24} />
                    <p className="text-[10px] font-bold text-gray-400 mt-2">Loading today's schedule...</p>
                  </div>
                )}
              </div>
            )}

            {/* 3. VIEW: QUESTIONNAIRE */}
            {selectedHubItem === 'survey' && (
              <div className="p-6 bg-blue-50 rounded-[2rem] border border-blue-100 animate-in slide-in-from-top-2 duration-300">
                <h3 className="font-black text-blue-800 mb-6 text-center text-xs uppercase tracking-widest">
                  Visitor Questionnaire
                </h3>
                {/* We pass the shopId prop here */}
                <VisitorQuestionnaire shopId={shopId} />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // HELPER COMPONENTS PLACED AT BOTTOM TO AVOID SCOPING ERRORS
  const HubButton = ({ icon, label, onClick }: { icon: any, label: string, onClick: () => void }) => (
    <button onClick={onClick} className="w-full flex items-center gap-3 p-4 bg-white border border-gray-100 rounded-xl shadow-sm text-left">
      <div className="text-blue-600">{icon}</div>
      <span className="text-sm font-bold text-gray-700">{label}</span>
    </button>
  );

